import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { query } from "@/services/services";
import { Panel, PanelHeader } from "@/components/common/Panel";
import { Button } from "@/components/ui/button";
import { formatTime, formatDate, daysUntil } from "@/lib/format";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ListChecks, RefreshCw, CheckCircle2 } from "lucide-react";

// Simulated eCourts-style daily cause list. "Sync" surfaces matters listed
// today/tomorrow and auto-creates hearing reminders (notifications + tasks).
export function CauseListPanel() {
  const { db, pushNotification, create, update } = useApp();
  const navigate = useNavigate();
  const [syncedAt, setSyncedAt] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [done, setDone] = useState({});

  const listed = useMemo(() => {
    return db.hearings
      .filter((h) => !h.outcome)
      .map((h) => ({ h, d: daysUntil(h.date) }))
      .filter((x) => x.d != null && x.d >= 0 && x.d <= 1)
      .sort((a, b) => new Date(a.h.date) - new Date(b.h.date))
      .map((x, i) => ({ ...x, serial: i + 1 }));
  }, [db.hearings]);

  const sync = () => {
    setSyncing(true);
    setTimeout(() => {
      let created = 0;
      const newDone = {};
      listed.forEach(({ h }) => {
        const m = query.matter(db, h.matterId);
        if (!m) return;
        // idempotent: only create a reminder once per hearing
        if (!h.reminderSyncedAt) {
          pushNotification({
            category: "Hearings",
            title: "Cause list reminder",
            body: `${m.caseNumber} — ${m.title} listed on ${formatDate(h.date, "dd MMM")} at ${formatTime(h.date)} (Item ${listed.find((l) => l.h.id === h.id)?.serial}). ${h.court}.`,
            matterId: h.matterId,
          });
          create("tasks", {
            matterId: h.matterId,
            title: `Prepare & appear — ${m.caseNumber} (cause list)`,
            assignee: m.leadId || "u_satish",
            priority: "High",
            status: "To Do",
            dueDate: h.date,
            category: "Hearing",
            description: `Auto-created from cause list sync. ${h.purpose} before ${h.judge}.`,
          });
          update("hearings", h.id, { reminderSyncedAt: new Date().toISOString() });
          created += 1;
        }
        newDone[h.id] = true;
      });
      setDone(newDone);
      setSyncedAt(new Date());
      setSyncing(false);
      toast.success(created ? `Cause list synced — ${created} hearing reminder${created === 1 ? "" : "s"} created.` : "Cause list already up to date.");
    }, 900);
  };

  return (
    <Panel data-testid="cause-list-panel">
      <PanelHeader title="Today's Cause List">
        <Button size="sm" variant="outline" onClick={sync} disabled={syncing || listed.length === 0} data-testid="sync-cause-list-btn">
          <RefreshCw className={cn("mr-1.5 h-3.5 w-3.5", syncing && "animate-spin")} /> {syncing ? "Syncing…" : "Sync Cause List"}
        </Button>
      </PanelHeader>
      <div className="flex items-center justify-between border-b border-line px-4 py-1.5 text-[11px] text-ink-muted">
        <span>Source: eCourts / High Court display board (simulated)</span>
        <span>{syncedAt ? `Last synced ${formatTime(syncedAt.toISOString())}` : "Not synced yet"}</span>
      </div>
      {listed.length === 0 ? (
        <div className="flex flex-col items-center justify-center px-4 py-8 text-center text-sm text-ink-2">
          <ListChecks className="mb-2 h-6 w-6 text-ink-muted" /> No matters listed for today or tomorrow.
        </div>
      ) : (
        <div className="divide-y divide-line">
          {listed.map(({ h, d, serial }) => {
            const m = query.matter(db, h.matterId);
            return (
              <button key={h.id} onClick={() => navigate(`/matters/${h.matterId}`)} className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-surface" data-testid={`cause-list-item-${h.id}`}>
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-secondary text-xs font-semibold text-ink-2">{serial}</div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-ink">{m?.title}</div>
                  <div className="truncate text-xs text-ink-2">{m?.caseNumber} · {h.court} · {h.purpose}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-medium text-copper">{d === 0 ? "Today" : "Tomorrow"} · {formatTime(h.date)}</div>
                  {done[h.id] && <div className="flex items-center justify-end gap-1 text-[10px] text-success"><CheckCircle2 className="h-3 w-3" /> reminder set</div>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Panel>
  );
}
